/* ============================================================
   Heideman · VIP "Inner Universe" — Adventskalender 2026
   Campaign interactions: countdown, scroll reveal, ring counter,
   starfield, gold ribbons, constellation, hero cross-fade.

   All tuning values come from data-* attributes on the sections
   so the theme editor stays the single source of truth.

   Mobile notes:
   - Canvas effects run on phones too (they carry the hero), but at
     DPR 1, fewer particles and a 25fps cap. Only devices with <=2
     cores or Save-Data skip them.
   - Canvas setup waits for the first paint so it never competes with
     LCP; below-the-fold backdrops load on approach.
   - Every rAF loop is gated by an IntersectionObserver and pauses
     with the tab, so off-screen sections cost nothing.

   Rendering notes:
   - No ctx.shadowBlur anywhere. It re-rasterises a blur kernel per
     draw call; with ~60 shadowed strokes per frame it put an 8-core
     desktop at 3fps. Glows are two-pass strokes (ribbons) or baked
     into pre-rendered sprites (stars, constellation dots) instead.
   - A quality governor watches the real rAF cadence after start and
     steps effects down (ambient canvases off → fully static) when
     frames stay slow, so the page degrades instead of freezing on
     machines without GPU acceleration.
   ============================================================ */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isSmall = window.matchMedia('(max-width: 767px)').matches;
  var isWeakDevice = (navigator.hardwareConcurrency || 4) <= 2;
  var saveData = (navigator.connection || {}).saveData === true;

  /* Quality tier, decided by the governor at the bottom from real frame
     times and remembered for the session, so the next navigation starts
     in the right mode straight away.
       0 = full effects · 1 = ambient canvases off · 2 = static page */
  var TIER_KEY = 'hv-quality-tier';
  var tier = 0;
  try {
    tier = parseInt(sessionStorage.getItem(TIER_KEY), 10) || 0;
  } catch (e) {}

  /* The starfield and gold ribbons are what make the hero feel alive, so
     they run on phones too — just lighter, and only after first paint.
     Only genuinely constrained devices skip them entirely. */
  var allowCanvas = !reduceMotion && !isWeakDevice && !saveData && tier < 2;

  if (tier >= 2 && !reduceMotion) {
    /* Cached verdict from a previous page: this machine can't hold the
       animations. The class kills them in CSS before they ever start. */
    document.documentElement.classList.add('hv-static');
  }

  /* These are ambient background effects, so they are drawn at a
     fraction of device resolution and scaled up by CSS. A wide desktop
     window was painting ~2.6 megapixels per frame — more than four times
     a phone — which is why desktop stuttered while mobile ran clean.
     0.75 keeps the ribbons soft (they have no hard edges) at ~45% of the
     pixel cost. */
  var RESOLUTION = 0.75;
  var DPR = Math.min(window.devicePixelRatio || 1, 1) * RESOLUTION;

  /* Star count follows area, so it needs no per-breakpoint tuning. */
  var starDensity = 34000;
  var starCap = 130;
  var frameBudget = isSmall ? 40 : 33;

  /* ---------- helpers ------------------------------------- */

  function num(el, attr, fallback) {
    if (!el) return fallback;
    var v = parseFloat(el.getAttribute(attr));
    return isNaN(v) ? fallback : v;
  }

  /* Every canvas loop registers here so the quality governor can shut
     loops down, cheapest-to-lose first, when frames stay slow. */
  var canvasLoops = [];

  /* Runs a rAF loop only while `el` is on screen. `budget` overrides the
     global frame cap — the ambient canvases repaint at 20fps. */
  function visibleLoop(el, step, budget) {
    var active = false;
    var stopped = false;
    var raf = null;
    var last = 0;
    var cap = budget || frameBudget;

    function frame(ts) {
      if (!active) return;
      raf = requestAnimationFrame(frame);
      /* cap frame rate — these are ambient effects */
      if (ts - last < cap) return;
      last = ts;
      step(ts);
    }

    var onScreen = false;

    function sync() {
      var shouldRun = onScreen && !document.hidden && !stopped;
      if (shouldRun && !active) {
        active = true;
        raf = requestAnimationFrame(frame);
      } else if (!shouldRun && active) {
        active = false;
        if (raf) cancelAnimationFrame(raf);
      }
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          onScreen = e.isIntersecting;
        });
        sync();
      },
      { threshold: 0 }
    );
    io.observe(el);

    /* Resume when the tab comes back — the old version only paused. */
    document.addEventListener('visibilitychange', sync);

    canvasLoops.push({
      el: el,
      stop: function () {
        stopped = true;
        sync();
        io.disconnect();
      }
    });
  }

  /* Defer non-essential work until after first paint so the hero renders
     as early as possible. Two rAFs = after the first real frame. */
  function afterPaint(fn) {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if ('requestIdleCallback' in window) {
          requestIdleCallback(fn, { timeout: 1200 });
        } else {
          setTimeout(fn, 1);
        }
      });
    });
  }

  /* ---------- lazy background images ---------------------- */

  /* Only the desktop marble wash is still a CSS background — it is a
     decorative texture behind the copy, not content. Every other image on
     the page is now a real <img> with loading="lazy", so the browser
     handles those without help.
     The wash would otherwise be fetched the moment the stylesheet parses,
     competing with the hero image, so it is attached as its section nears
     the viewport. */
  function initLazyBackgrounds() {
    var targets = document.querySelectorAll('.hv-split-wash');
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('hv-bg-ready');
      });
      return;
    }

    var bgIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          /* Observe the section but flag the wash child. */
          var el = e.target.__hvBackdrop || e.target;
          el.classList.add('hv-bg-ready');
          bgIO.unobserve(e.target);
        });
      },
      /* Start loading a screen early so the image is there on arrival. */
      { rootMargin: '150% 0px' }
    );

    targets.forEach(function (el) {
      /* The wash is absolutely positioned inside an overflow:hidden
         section, so observing it directly is unreliable — the observer
         never fires and the image never loads. Watch the section instead. */
      var anchor = el.closest('section') || el;
      anchor.__hvBackdrop = el;
      bgIO.observe(anchor);
    });
  }

  function fitCanvas(cv) {
    cv.width = Math.round(cv.offsetWidth * DPR);
    cv.height = Math.round(cv.offsetHeight * DPR);
  }

  /* A glow baked once into a tiny offscreen canvas; the frame loops just
     drawImage it. Replaces ctx.shadowBlur, which re-rasterises a blur
     kernel on every draw call. `coreR`/`haloR` are in sprite pixels; the
     draw call scales the sprite to the particle's size. */
  function makeGlowSprite(coreR, haloR, core, mid, edge) {
    var size = Math.ceil(haloR * 2);
    var c = document.createElement('canvas');
    c.width = size;
    c.height = size;
    var ctx = c.getContext('2d');
    var grad = ctx.createRadialGradient(haloR, haloR, 0, haloR, haloR, haloR);
    grad.addColorStop(0, core);
    grad.addColorStop(Math.min(0.99, coreR / haloR), mid);
    grad.addColorStop(1, edge);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
    return c;
  }

  /* ---------- pause off-screen CSS animations -------------- */

  /* Ambient loops (nebula drift, gold sheen, button glow, Ken Burns) keep
     the compositor busy even when their section is far off screen — most
     of the page's running animations were off-screen at any moment.
     Toggling animation-play-state per section costs nothing and keeps
     only what is actually visible alive. */
  function initAnimationGating() {
    var hosts = document.querySelectorAll(
      '.hvip > section, .hvip > header, .hvip > footer, .hvip > div'
    );
    if (!hosts.length || !('IntersectionObserver' in window)) return;

    var animIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          e.target.classList.toggle('hv-anim-paused', !e.isIntersecting);
        });
      },
      { rootMargin: '25% 0px' }
    );
    hosts.forEach(function (el) {
      animIO.observe(el);
    });
  }

  /* ---------- 1 · sticky nav ------------------------------ */

  var nav = document.querySelector('[data-hv-nav]');
  if (nav) {
    var solidClass = nav.getAttribute('data-hv-nav-solid') || 'hv-nav-solid';
    var navSolid = null;
    var navQueued = false;

    function applyNavState() {
      navQueued = false;
      /* Read the offset from whichever element actually scrolls. */
      var y = window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
      var on = y > 60;
      if (on === navSolid) return;
      navSolid = on;
      nav.classList.toggle(solidClass, on);
    }

    function onNavScroll() {
      /* Coalesce to one class write per frame instead of per event. */
      if (navQueued) return;
      navQueued = true;
      requestAnimationFrame(applyNavState);
    }

    /* Listen on both: if a stylesheet ever makes <body> the scroll
       container again, window never emits scroll and the bar would
       silently stop reacting — which is exactly what happened before. */
    window.addEventListener('scroll', onNavScroll, { passive: true });
    document.addEventListener('scroll', onNavScroll, { passive: true, capture: true });
    applyNavState();
  }

  /* ---------- 2 · headline character reveal --------------- */

  var h1 = document.querySelector('[data-hv-headline]');
  if (h1 && !reduceMotion) {
    var parts = h1.querySelectorAll('[data-hv-word]');
    parts.forEach(function (el, wi) {
      var txt = el.textContent;
      var out = '';
      for (var i = 0; i < txt.length; i++) {
        var delay = (wi * 0.34 + i * 0.055).toFixed(3);
        var ch = txt[i] === ' ' ? '&nbsp;' : txt[i];
        out +=
          '<span class="hv-ch" style="transition:opacity .9s cubic-bezier(.22,.61,.36,1) ' +
          delay +
          's,transform .9s cubic-bezier(.22,.61,.36,1) ' +
          delay +
          's">' +
          ch +
          '</span>';
      }
      el.innerHTML = out;
    });
    setTimeout(function () {
      h1.querySelectorAll('.hv-ch').forEach(function (c) {
        c.style.opacity = 1;
        c.style.transform = 'translate3d(0,0,0)';
      });
    }, 160);
  }

  /* ---------- 3 · scroll reveal --------------------------- */

  var revealables = document.querySelectorAll('.hv-rv');
  if (revealables.length) {
    if (reduceMotion) {
      revealables.forEach(function (el) {
        el.classList.add('is-in');
      });
    } else {
      var revealIO = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (e, i) {
            if (!e.isIntersecting) return;
            setTimeout(function () {
              e.target.classList.add('is-in');
            }, i * 90);
            revealIO.unobserve(e.target);
          });
        },
        { threshold: 0.14, rootMargin: '0px 0px -8% 0px' }
      );
      revealables.forEach(function (el) {
        revealIO.observe(el);
      });
    }
  }

  /* ---------- 4 · countdown ------------------------------- */

  var cd = document.querySelector('[data-hv-countdown]');
  if (cd) {
    var target = new Date(cd.getAttribute('data-hv-countdown')).getTime();
    var out = {
      d: cd.querySelector('[data-hv-cd="d"]'),
      h: cd.querySelector('[data-hv-cd="h"]'),
      m: cd.querySelector('[data-hv-cd="m"]'),
      s: cd.querySelector('[data-hv-cd="s"]')
    };

    function pad(n) {
      return n < 10 ? '0' + n : '' + n;
    }

    function tickCountdown() {
      if (isNaN(target)) return;
      var t = target - Date.now();
      if (t < 0) t = 0;
      if (out.d) out.d.textContent = pad(Math.floor(t / 864e5));
      if (out.h) out.h.textContent = pad(Math.floor(t / 36e5) % 24);
      if (out.m) out.m.textContent = pad(Math.floor(t / 6e4) % 60);
      if (out.s) out.s.textContent = pad(Math.floor(t / 1e3) % 60);
    }

    tickCountdown();
    setInterval(tickCountdown, 1000);
  }

  /* ---------- 5 · ring counter --------------------------- */

  var ring = document.querySelector('[data-hv-ring]');
  if (ring) {
    var total = num(ring, 'data-hv-ring-total', 100);
    var free = num(ring, 'data-hv-ring-free', 100);
    var prg = ring.querySelector('[data-hv-ring-prg]');
    var cnt = ring.querySelector('[data-hv-ring-count]');
    var done = false;

    var ringIO = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting || done) return;
          done = true;

          var pct = total > 0 ? free / total : 0;
          if (prg) prg.style.strokeDashoffset = 302 * (1 - pct);

          if (!cnt) return;
          if (reduceMotion) {
            cnt.textContent = Math.round(free);
            return;
          }
          var start = performance.now();
          var dur = 1800;
          (function step(now) {
            var p = Math.min(1, (now - start) / dur);
            var eased = 1 - Math.pow(1 - p, 3);
            cnt.textContent = Math.round(free * eased);
            if (p < 1) requestAnimationFrame(step);
          })(start);
        });
      },
      { threshold: 0.4 }
    );
    ringIO.observe(ring);
  }

  /* ---------- 6 · hero cross-fade ------------------------ */

  var heroBg = document.querySelector('[data-hv-hero-bg]');
  if (heroBg) {
    var swapAfter = num(heroBg, 'data-hv-swap-after', 5600);
    if (reduceMotion) {
      heroBg.classList.add('is-swapped');
    } else {
      setTimeout(function () {
        heroBg.classList.add('is-swapped');
      }, swapAfter);
    }
  }

  /* ---------- 7 · cursor glow + parallax (desktop) ------- */

  var hero = document.querySelector('[data-hv-hero]');
  var glow = hero && hero.querySelector('[data-hv-glow]');
  if (hero && glow && !reduceMotion && window.matchMedia('(hover: hover)').matches) {
    hero.addEventListener('mousemove', function (e) {
      var r = hero.getBoundingClientRect();
      glow.style.left = e.clientX - r.left + 'px';
      glow.style.top = e.clientY - r.top + 'px';
      glow.style.opacity = 1;
    });
    hero.addEventListener('mouseleave', function () {
      glow.style.opacity = 0;
    });
  }

  /* These run regardless of whether canvas effects are enabled. */
  afterPaint(initLazyBackgrounds);
  afterPaint(initAnimationGating);

  /* ---------- 11 · quality governor ---------------------- */

  /* hardwareConcurrency says little about real rendering speed — an
     8-core desktop without GPU acceleration still landed at 3fps. So the
     page watches its own rAF cadence shortly after start and steps the
     effects down until frames are healthy:
       tier 1 — ambient canvases off (constellation, scarcity ribbons)
       tier 2 — all canvases off, page fully static (html.hv-static)
     The verdict is kept in sessionStorage so the next navigation starts
     in the right mode without re-measuring. */
  function startGovernor() {
    if (reduceMotion || tier >= 2) return;

    /* A window is time-boxed, not frame-counted: at 3fps, 50 frames
       would take 17 seconds — the page must react faster than that. */
    var WINDOW_MS = 1800;
    var WINDOW_FRAMES = 50; /* early exit for healthy machines */
    var MIN_SAMPLES = 8;
    var SLOW = 34; /* ms — above a 30Hz display's cadence, well into jank */
    var samples = [];
    var last = 0;
    var winStart = 0;
    var strikes = 0;

    function stopLoops(ids) {
      canvasLoops.forEach(function (l) {
        if (ids && ids.indexOf(l.el.id) === -1) return;
        l.stop();
        l.el.style.display = 'none';
      });
    }

    function evaluate() {
      samples.sort(function (a, b) {
        return a - b;
      });
      /* p75, not median: janky pages are bimodal — one 100ms paint frame
         followed by a couple of cheap 17ms ticks. The median lands on the
         cheap ticks and calls that healthy; the 75th percentile sees the
         stutter the user feels. */
      var p75 = samples[(samples.length * 3) >> 2];
      samples.length = 0;
      last = 0;
      winStart = 0;
      if (p75 <= SLOW) return; /* healthy — keep everything, stop watching */

      /* One slow window can be a fluke (decode burst, extension, GC).
         Only a second slow window in a row costs the page its effects. */
      strikes++;
      if (strikes < 2) {
        requestAnimationFrame(sample);
        return;
      }
      strikes = 0;

      tier++;
      if (tier === 1 && allowCanvas) {
        stopLoops(['hv-constel', 'hv-ribbons2']);
        try {
          sessionStorage.setItem(TIER_KEY, '1');
        } catch (e) {}
        /* Re-measure with the ambient canvases gone. */
        requestAnimationFrame(sample);
        return;
      }
      tier = 2;
      stopLoops(null);
      document.documentElement.classList.add('hv-static');
      try {
        sessionStorage.setItem(TIER_KEY, '2');
      } catch (e) {}
    }

    function sample(ts) {
      if (!winStart) winStart = ts;
      if (last) {
        var d = ts - last;
        /* Tab switches and long idles produce huge deltas — not jank. */
        if (d < 250) samples.push(d);
      }
      last = ts;
      if (
        samples.length >= WINDOW_FRAMES ||
        (ts - winStart > WINDOW_MS && samples.length >= MIN_SAMPLES)
      ) {
        evaluate();
        return;
      }
      requestAnimationFrame(sample);
    }

    requestAnimationFrame(sample);
  }

  /* The governor must not measure the load phase: image decodes, font
     swaps and third-party scripts make early frames slow on every
     machine, and a verdict from that window would strip the effects from
     perfectly healthy devices (observed in testing). Start after the
     load event settles. It runs even when canvas is disabled: CSS
     animations alone can sink a weak machine, and tier 2 shuts those
     down too. */
  function armGovernor() {
    setTimeout(startGovernor, 1500);
  }
  if (document.readyState === 'complete') {
    armGovernor();
  } else {
    window.addEventListener('load', armGovernor, { once: true });
  }

  if (!allowCanvas) return;

  /* Everything below paints pixels. Defer the whole block past the
     first frame so canvas sizing and the initial draw never delay LCP. */
  afterPaint(function () {

  /* ---------- 8 · gold light ribbons --------------------- */

  function makeRibbons(cv, bands, segments, span, budget) {
    var g = cv.getContext('2d');
    var W = 0;
    var H = 0;
    var t0 = 0;

    function resize() {
      fitCanvas(cv);
      W = cv.width;
      H = cv.height;
    }

    function drawBand(b, t) {
      var head = (t * b.sp) % 1;
      for (var i = 0; i < segments; i++) {
        var u = i / segments;
        var d = Math.abs(((u - head) + 1.5) % 1 - 0.5);
        if (d > span) continue;
        var k = 1 - d / span;
        var k2 = k * k;
        /* The tail of each band fades below visibility well before the
           span cutoff — skip those segments entirely (~a third of all
           strokes, at zero visual cost). */
        if (b.a * k2 < 0.03) continue;
        var a1 = u * Math.PI * 2 + b.ph;
        var a2 = (u + 1 / segments) * Math.PI * 2 + b.ph;

        function pt(a) {
          var wob = 1 + Math.sin(a * 3 + t * 0.0005) * 0.09;
          var x = Math.cos(a) * b.rx * wob;
          var y = Math.sin(a) * b.ry * wob;
          return [
            (b.cx + x * Math.cos(b.rot) - y * Math.sin(b.rot)) * W,
            (b.cy + x * Math.sin(b.rot) + y * Math.cos(b.rot)) * H
          ];
        }

        var p = pt(a1);
        var q = pt(a2);
        var w = b.w * DPR * (0.5 + k2 * 2.6);
        g.beginPath();
        g.moveTo(p[0], p[1]);
        g.lineTo(q[0], q[1]);
        /* Glow pass: the same path stroked wide and faint underneath the
           core replaces the shadowBlur halo. Two plain strokes cost a
           fraction of one blurred stroke, and with 'lighter' compositing
           the overlap still reads as a soft gold shine. */
        g.lineWidth = w * 3.2;
        g.strokeStyle = 'rgba(255,205,120,' + b.a * k2 * 0.26 + ')';
        g.stroke();
        g.lineWidth = w;
        g.strokeStyle =
          'rgba(255,' +
          (186 + Math.round(34 * k2)) +
          ',' +
          (74 + Math.round(56 * k2)) +
          ',' +
          b.a * k2 * 0.92 +
          ')';
        g.stroke();
      }
    }

    window.addEventListener('resize', resize);
    resize();

    visibleLoop(cv, function (ts) {
      if (!t0) t0 = ts;
      var t = ts - t0;
      g.clearRect(0, 0, W, H);
      g.globalCompositeOperation = 'lighter';
      g.lineCap = 'round';
      for (var i = 0; i < bands.length; i++) drawBand(bands[i], t);
    }, budget);
  }

  var ribbons1 = document.getElementById('hv-ribbons');
  if (ribbons1) {
    /* The bands orbit the packshot, so their centres are read from where
       the image actually sits rather than hardcoded. On desktop the shot
       occupies the right-hand column (centre ~0.71 x, 0.5 y) — which is
       what the original numbers were tuned for. On mobile it is a band
       across the top (centre ~0.5 x, 0.18 y), and those same numbers put
       the ribbons in empty space beside and below the image, drifting over
       the copy instead of wrapping the calendar. */
    var lane = document.querySelector('.hv-lay-a');
    var host = ribbons1.getBoundingClientRect();
    var cx = 0.71;
    var cy = 0.5;
    var scale = 1;
    if (lane && host.width > 0 && host.height > 0) {
      var lr = lane.getBoundingClientRect();
      cx = (lr.left + lr.right) / 2 / host.width;
      cy = (lr.top + lr.bottom) / 2 - host.top;
      cy = cy / host.height;
      /* Keep the orbit proportional to the image, not the canvas. */
      scale = Math.min(1.35, Math.max(0.5, lr.height / host.height / 0.62));
    }

    makeRibbons(
      ribbons1,
      [
        { cx: cx + 0.03, cy: cy, rx: 0.2 * scale, ry: 0.3 * scale, rot: -0.5, w: 2.4, a: 0.9, sp: 0.0004, ph: 0 },
        { cx: cx, cy: cy + 0.08 * scale, rx: 0.26 * scale, ry: 0.2 * scale, rot: 0.35, w: 1.6, a: 0.7, sp: 0.0003, ph: 1.9 },
        { cx: cx + 0.07, cy: cy + 0.16 * scale, rx: 0.17 * scale, ry: 0.26 * scale, rot: -1.1, w: 3.0, a: 0.55, sp: 0.00023, ph: 3.4 },
        { cx: cx - 0.02, cy: cy - 0.09 * scale, rx: 0.13 * scale, ry: 0.17 * scale, rot: 0.9, w: 1.2, a: 0.6, sp: 0.00052, ph: 4.8 }
      ],
      46,
      0.16
    );
  }

  /* Ambient canvases (scarcity ribbons, constellation) are the first to
     go when the governor steps down — and they repaint at 20fps: they sit
     behind copy, nobody reads them at 30. */
  var AMBIENT_BUDGET = 50;

  var ribbons2 = document.getElementById('hv-ribbons2');
  if (ribbons2 && tier < 1) {
    makeRibbons(
      ribbons2,
      [
        { cx: 0.26, cy: 0.46, rx: 0.34, ry: 0.3, rot: -0.35, w: 3.0, a: 1.0, sp: 0.00022, ph: 0.4 },
        { cx: 0.2, cy: 0.6, rx: 0.26, ry: 0.22, rot: 0.5, w: 2.0, a: 0.8, sp: 0.00017, ph: 2.6 },
        { cx: 0.34, cy: 0.34, rx: 0.3, ry: 0.18, rot: -0.9, w: 3.4, a: 0.62, sp: 0.00013, ph: 4.2 },
        { cx: 0.6, cy: 0.7, rx: 0.36, ry: 0.24, rot: 0.2, w: 1.4, a: 0.35, sp: 0.00011, ph: 5.5 }
      ],
      52,
      0.26,
      AMBIENT_BUDGET
    );
  }

  /* ---------- 9 · starfield ------------------------------ */

  var stars = document.getElementById('hv-stars');
  if (stars) {
    var sx = stars.getContext('2d');
    var SW = 0;
    var SH = 0;
    var field = [];
    var shooting = [];

    /* Warm stars used to carry shadowBlur — the halo is baked into the
       sprite now. Cool stars get a soft-edged dot, which reads the same
       as the old antialiased arc. Twinkle happens via globalAlpha. */
    var warmSprite = makeGlowSprite(
      6, 24,
      'rgba(230,211,172,1)', 'rgba(210,180,120,0.4)', 'rgba(198,166,100,0)'
    );
    var coolSprite = makeGlowSprite(
      7, 12,
      'rgba(245,238,226,1)', 'rgba(245,238,226,0.6)', 'rgba(245,238,226,0)'
    );

    function buildField() {
      field = [];
      var n = Math.min(starCap, Math.round((SW * SH) / starDensity));
      for (var i = 0; i < n; i++) {
        field.push({
          x: Math.random() * SW,
          y: Math.random() * SH,
          r: (Math.random() * 1.25 + 0.25) * DPR,
          a: Math.random(),
          sp: Math.random() * 0.012 + 0.003,
          vx: (Math.random() - 0.5) * 0.055,
          vy: (Math.random() - 0.5) * 0.055,
          warm: Math.random() < 0.16
        });
      }
    }

    function resizeStars() {
      fitCanvas(stars);
      SW = stars.width;
      SH = stars.height;
      buildField();
    }

    window.addEventListener('resize', resizeStars);
    resizeStars();

    visibleLoop(stars, function () {
      sx.clearRect(0, 0, SW, SH);
      for (var i = 0; i < field.length; i++) {
        var s = field[i];
        s.a += s.sp;
        s.x += s.vx;
        s.y += s.vy;
        if (s.x < 0) s.x = SW;
        if (s.x > SW) s.x = 0;
        if (s.y < 0) s.y = SH;
        if (s.y > SH) s.y = 0;
        var al = 0.2 + Math.abs(Math.sin(s.a)) * 0.62;
        /* Halo scale matches the sprite's halo/core ratio, so a warm
           star's glow spans ~4 radii like the old shadowBlur did. */
        var half = s.r * (s.warm ? 4 : 1.7);
        sx.globalAlpha = s.warm ? al : al * 0.62;
        sx.drawImage(
          s.warm ? warmSprite : coolSprite,
          s.x - half, s.y - half, half * 2, half * 2
        );
      }
      sx.globalAlpha = 1;

      if (Math.random() < 0.0035 && shooting.length < 2) {
        shooting.push({
          x: Math.random() * SW * 0.7,
          y: Math.random() * SH * 0.4,
          l: 0,
          ml: (160 + Math.random() * 180) * DPR,
          sp: (6 + Math.random() * 5) * DPR
        });
      }
      for (var j = shooting.length - 1; j >= 0; j--) {
        var m = shooting[j];
        m.l += m.sp;
        m.x += m.sp * 0.9;
        m.y += m.sp * 0.42;
        var grad = sx.createLinearGradient(m.x, m.y, m.x - m.l * 0.9, m.y - m.l * 0.42);
        grad.addColorStop(0, 'rgba(255,246,226,.9)');
        grad.addColorStop(1, 'rgba(255,246,226,0)');
        sx.strokeStyle = grad;
        sx.lineWidth = 1.3 * DPR;
        sx.beginPath();
        sx.moveTo(m.x, m.y);
        sx.lineTo(m.x - m.l * 0.9, m.y - m.l * 0.42);
        sx.stroke();
        if (m.l > m.ml) shooting.splice(j, 1);
      }
    });
  }

  /* ---------- 10 · constellation ------------------------- */

  var constel = document.getElementById('hv-constel');
  if (constel && tier < 1) {
    var c2 = constel.getContext('2d');
    var CW = 0;
    var CH = 0;
    var nodes = [];

    var nodeSprite = makeGlowSprite(
      6, 10,
      'rgba(198,166,100,1)', 'rgba(198,166,100,0.85)', 'rgba(198,166,100,0)'
    );

    /* Each line used to be its own beginPath/strokeStyle/stroke — ~200
       draw calls per frame. The lines are collected into a few alpha
       buckets instead and each bucket is stroked once. The alpha step
       between buckets is invisible at 0.16 peak opacity. */
    var BUCKETS = 4;
    var bucketSegs = [];
    var bucketLen = [];
    for (var bi = 0; bi < BUCKETS; bi++) {
      bucketSegs.push([]);
      bucketLen.push(0);
    }

    function resizeConstel() {
      fitCanvas(constel);
      CW = constel.width;
      CH = constel.height;
      nodes = [];
      for (var i = 0; i < 40; i++) {
        nodes.push({
          x: Math.random() * CW,
          y: Math.random() * CH,
          vx: (Math.random() - 0.5) * 0.16,
          vy: (Math.random() - 0.5) * 0.16
        });
      }
    }

    window.addEventListener('resize', resizeConstel);
    resizeConstel();

    visibleLoop(constel, function () {
      c2.clearRect(0, 0, CW, CH);
      var maxDist = 150 * DPR;
      var b;
      for (b = 0; b < BUCKETS; b++) bucketLen[b] = 0;

      c2.globalAlpha = 0.55;
      var dotHalf = 1.9 * DPR;
      for (var i = 0; i < nodes.length; i++) {
        var p = nodes[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > CW) p.vx *= -1;
        if (p.y < 0 || p.y > CH) p.vy *= -1;
        c2.drawImage(nodeSprite, p.x - dotHalf, p.y - dotHalf, dotHalf * 2, dotHalf * 2);
        for (var j = i + 1; j < nodes.length; j++) {
          var q = nodes[j];
          var dx = p.x - q.x;
          var dy = p.y - q.y;
          var d = Math.sqrt(dx * dx + dy * dy);
          if (d >= maxDist) continue;
          b = ((1 - d / maxDist) * BUCKETS) | 0;
          if (b >= BUCKETS) b = BUCKETS - 1;
          var seg = bucketSegs[b];
          seg[bucketLen[b]++] = p.x;
          seg[bucketLen[b]++] = p.y;
          seg[bucketLen[b]++] = q.x;
          seg[bucketLen[b]++] = q.y;
        }
      }
      c2.globalAlpha = 1;

      c2.lineWidth = 0.6 * DPR;
      for (b = 0; b < BUCKETS; b++) {
        var n = bucketLen[b];
        if (!n) continue;
        var segs = bucketSegs[b];
        c2.beginPath();
        for (var k = 0; k < n; k += 4) {
          c2.moveTo(segs[k], segs[k + 1]);
          c2.lineTo(segs[k + 2], segs[k + 3]);
        }
        c2.strokeStyle = 'rgba(198,166,100,' + 0.16 * ((b + 0.5) / BUCKETS) + ')';
        c2.stroke();
      }
    }, AMBIENT_BUDGET);
  }
  });
})();
